import React from 'react'
import { Box, Card, CardContent, Typography } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { NavLink } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ChevronRight, RadioTower } from 'lucide-react'
import cl from 'classnames'
import moment from 'moment'
import { useStyles } from './styles'

export const DeviceGatewayCard = ({
    gateway,
    isLoading,
    isError,
    gwId,
    deviceId
}) => {
    const classes = useStyles()
    const { theme, locale } = useSelector(state => state.main)

    const title = <Typography>{locale.devices.connectedGateway}</Typography>

    if (isLoading) {
        return (
            <Box>
                {title}
                <Card className={classes.gatewayCard}>
                    <CardContent className={classes.gatewayCardContent}>
                        <Box className={classes.gatewayIdentity}>
                            <Skeleton
                                variant="rect"
                                width={44}
                                height={44}
                                className={classes.gatewayIconPlateSkeleton}
                            />
                            <Box className={classes.gatewayIdentityText}>
                                <Skeleton variant="text" width={190} />
                                <Skeleton variant="text" width={110} />
                            </Box>
                        </Box>
                        <Box className={classes.gatewayFooter}>
                            <Skeleton variant="text" width={90} />
                            <Skeleton variant="text" width={140} />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (isError || !gateway) {
        return (
            <Box>
                {title}
                <Card className={classes.gatewayCard}>
                    <CardContent className={classes.gatewayCardContent}>
                        <Box className={classes.gatewayIdentity}>
                            <Box className={classes.gatewayIconPlate}>
                                <RadioTower
                                    size={22}
                                    color={theme.quaternary}
                                />
                            </Box>
                            <Box className={classes.gatewayIdentityText}>
                                <Typography
                                    className={classes.gatewaySerial}
                                    title={gwId}
                                    noWrap
                                >
                                    {gwId}
                                </Typography>
                                <Typography className={classes.gatewaySubline}>
                                    {locale.devices.gatewayNotFound}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    const status = gateway.data?.tti?.status
    const lastSignal = gateway.data?.tti?.last_status_received_at

    let statusColor = theme.quaternary
    let statusText = 'N/A'
    if (status) {
        const isConnected = status === 'connected'
        statusColor = isConnected ? theme.active : theme.error
        statusText = isConnected
            ? locale.common.online
            : locale.common.disconnected
    }

    return (
        <Box>
            {title}
            <NavLink
                className={classes.gatewayCardLink}
                to={`/network-devices/${gateway.id}?return=${encodeURIComponent(
                    `/devices/${deviceId}/overview`
                )}`}
            >
                <Card
                    className={cl(
                        classes.gatewayCard,
                        classes.gatewayCardInteractive
                    )}
                >
                    <CardContent className={classes.gatewayCardContent}>
                        <Box className={classes.gatewayIdentity}>
                            <Box className={classes.gatewayIconPlate}>
                                <RadioTower size={22} color={theme.primary} />
                            </Box>
                            <Box className={classes.gatewayIdentityText}>
                                <Typography
                                    className={classes.gatewaySerial}
                                    title={gateway.serial_number}
                                    noWrap
                                >
                                    {gateway.serial_number}
                                </Typography>
                                <Typography
                                    className={classes.gatewaySubline}
                                    noWrap
                                >
                                    {gateway.location_name ||
                                        gateway.router_type ||
                                        locale.devices.notAssigned}
                                </Typography>
                            </Box>
                            <ChevronRight
                                size={20}
                                color={theme.quaternary}
                                className={classes.gatewayChevron}
                            />
                        </Box>

                        <Box className={classes.gatewayFooter}>
                            <Box className={classes.gatewayStatus}>
                                <Box
                                    className={classes.gatewayStatusDot}
                                    style={{ backgroundColor: statusColor }}
                                />
                                <Typography
                                    className={classes.gatewayStatusText}
                                >
                                    {statusText}
                                </Typography>
                            </Box>
                            {lastSignal ? (
                                <Typography
                                    className={classes.gatewayLastSignal}
                                    noWrap
                                >
                                    {locale.devices.gatewayLastSignal}:{' '}
                                    {moment(lastSignal).format(
                                        'DD.MM.YYYY HH:mm'
                                    )}
                                </Typography>
                            ) : null}
                        </Box>
                    </CardContent>
                </Card>
            </NavLink>
        </Box>
    )
}
